import { Link, useParams, Navigate } from 'react-router-dom';
import { Phone, Clock, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { COMPANY } from '../mock/data';
import { POST_BY_SLUG, POSTS } from '../data/blog';

export default function BlogPost() {
  const { slug } = useParams();
  const post = POST_BY_SLUG[slug];
  if (!post) return <Navigate to="/blog" replace />;

  const canonical = `https://betterfenceanddeck.org/blog/${post.slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: `https://betterfenceanddeck.org${post.hero}`,
    author: { '@type': 'Organization', name: 'Better Fence & Deck' },
    publisher: { '@type': 'Organization', name: 'Better Fence & Deck' },
    datePublished: post.date,
    mainEntityOfPage: canonical,
  };

  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="bg-ink text-cream min-h-screen">
      <SEO title={`${post.title} | Better Fence & Deck`} description={post.excerpt} canonical={canonical} ogImage={`https://betterfenceanddeck.org${post.hero}`} jsonLd={jsonLd} />
      <Navbar />

      <article className="pt-40 pb-16">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-gold hover:text-gold-light"><ArrowLeft className="w-4 h-4" /> All articles</Link>
          <div className="mt-4 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-white/15 text-cream/70">{t}</span>
            ))}
          </div>
          <h1 className="mt-4 font-display uppercase text-3xl md:text-5xl leading-[1.05] tracking-tight text-cream">
            {post.title}
          </h1>
          <div className="mt-3 flex items-center gap-3 text-cream/50 text-sm">
            <span>{new Date(post.date).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readMinutes} min read</span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-5 md:px-8 mt-8">
          <div className="aspect-[16/9] rounded-md overflow-hidden border border-white/10">
            <img src={post.hero} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-5 md:px-8 mt-10 space-y-4 text-cream/80 leading-relaxed">
          <p className="text-lg text-cream/90 italic border-l-2 border-gold pl-4">{post.excerpt}</p>
          {post.body.map((b, i) => (
            b.h ? (
              <h2 key={i} className="font-display uppercase text-2xl text-cream mt-8 tracking-tight">{b.h}</h2>
            ) : (
              <p key={i}>{b.p}</p>
            )
          ))}

          <div className="mt-10 p-5 rounded-md bg-gold text-ink">
            <div className="font-display uppercase text-xl">Need a quote in the ArkLaTex?</div>
            <p className="mt-1 text-sm">Call {COMPANY.phone} or fill out our online form for a free written estimate.</p>
            <div className="mt-3 flex gap-2">
              <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-cream rounded font-bold text-sm uppercase tracking-widest hover:bg-ink-soft transition-colors"><Phone className="w-4 h-4" /> Call Now</a>
              <Link to="/#quote" className="inline-flex items-center gap-2 px-4 py-2 border-2 border-ink text-ink rounded font-bold text-sm uppercase tracking-widest hover:bg-ink hover:text-cream transition-colors">Get Quote</Link>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-5 md:px-8 mt-16 pt-10 border-t border-white/10">
          <div className="eyebrow">Keep reading</div>
          <div className="mt-4 grid md:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="block group">
                <div className="aspect-[16/10] rounded-md overflow-hidden border border-white/10">
                  <img src={r.hero} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="mt-3 font-display uppercase text-lg text-cream group-hover:text-gold transition-colors">{r.title}</div>
              </Link>
            ))}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
